import clsx from 'clsx';
import { ReactNode, useContext } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { A11y, Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperProps } from 'swiper/react';
import { twMerge } from 'tailwind-merge';

import { Arrow } from 'components/icons';

import { Button, ButtonVariant } from './Button';
import { LocaleContext } from './Layout';

type Props = {
  children: ReactNode;
  className?: string;
  navButtonVariant?: ButtonVariant;
  nextNavEl?: string;
  noNavigation?: boolean;
  prevNavEl?: string;
} & SwiperProps;

export const NavigationButtons = ({
  buttonVariant = 'primary',
  className,
  nextNavEl,
  prevNavEl,
}: {
  buttonVariant?: ButtonVariant;
  className?: string;
  nextNavEl?: string;
  prevNavEl?: string;
}) => {
  return (
    <div className={twMerge(clsx('flex w-fit flex-row gap-8', className))}>
      <Button
        className={twMerge(clsx('flex items-center justify-center p-8', prevNavEl))}
        id={prevNavEl}
        variant={buttonVariant}
      >
        <Arrow className="h-24 w-24 rotate-180" />
      </Button>
      <Button
        className={twMerge(clsx('flex items-center justify-center p-8', nextNavEl))}
        id={nextNavEl}
        variant={buttonVariant}
      >
        <Arrow className="h-24 w-24" />
      </Button>
    </div>
  );
};

export const SwiperCarousel = ({
  children,
  className,
  navButtonVariant,
  nextNavEl,
  noNavigation,
  prevNavEl,
  ...swiperProps
}: Props) => {
  const locale = useContext(LocaleContext);
  return (
    <div className={className}>
      <Swiper
        breakpoints={{
          320: { slidesPerView: 'auto', spaceBetween: 16 },
          576: { slidesPerView: 'auto', spaceBetween: 16 },
          768: { slidesPerView: 'auto', spaceBetween: 16 },
          992: { slidesPerView: 2, spaceBetween: 32 },
          1200: { slidesPerView: 3, spaceBetween: 32 },
        }}
        slidesOffsetAfter={32}
        modules={[A11y, Keyboard, Navigation]}
        a11y={{
          prevSlideMessage: {
            en: 'Previous slide',
            fr: 'Diapositive précédente',
          }[locale],
          nextSlideMessage: { en: 'Next slide', fr: 'Diapositive suivante' }[locale],
        }}
        keyboard
        navigation={{ enabled: true, nextEl: `.${nextNavEl}`, prevEl: `.${prevNavEl}` }}
        {...swiperProps}
      >
        {children}
      </Swiper>
      {!noNavigation && (
        <NavigationButtons
          className="mt-40"
          nextNavEl={nextNavEl}
          prevNavEl={prevNavEl}
          buttonVariant={navButtonVariant}
        />
      )}
    </div>
  );
};
